import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowUp,
  Calendar,
  CheckSquare,
  ChevronDown,
  InfoIcon,
  Package,
  Pencil,
  Search,
  ShoppingBag,
  StickyNote,
  Truck,
  User,
  X,
} from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { API_CONFIG } from '@/shared/constants/api';
import HttpService from '@/shared/services/http.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FilterDropdown } from '@/components/ui/filter-dropdown';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePageHeader } from '@/contexts/HeaderContext';
import './AllSlotList.css';
import { getDaysAgoAndColor } from '@/lib/getDaysAgoAndColor';
import { PackageData, storageStyle, carrierStyle, formatDate } from '@/lib/packageUtils';
import { PackageDetailsModal } from '@/components/PackageDetailsModal';

// ─── Types ───────────────────────────────────────────────────────────────────
// ─── Helpers ─────────────────────────────────────────────────────────────────
const STORAGE_FILTERS = [
  {
    value: 'all',
    labelKey: 'all',
    active: 'bg-orange-600 text-white',
    inactive: 'bg-orange-200 text-orange-700 hover:bg-orange-300',
  },
  {
    value: 'CENTER',
    labelKey: 'center',
    active: 'bg-purple-600 text-white',
    inactive: 'bg-purple-200 text-purple-700 hover:bg-purple-300',
  },
  {
    value: 'LARGE',
    labelKey: 'large',
    active: 'bg-yellow-600 text-white',
    inactive: 'bg-yellow-200 text-yellow-700 hover:bg-yellow-300',
  },
  {
    value: 'SAFE',
    labelKey: 'safe',
    active: 'bg-teal-600 text-white',
    inactive: 'bg-teal-200 text-teal-700 hover:bg-teal-300',
  },
];

const ALL_FRAMES = ['All', ...Array.from({ length: 50 }, (_, i) => `F${i + 1}`)];
const ALL_BOXES = ['All', ...Array.from({ length: 50 }, (_, i) => `Box-${i + 1}`)];

// ─── Component ────────────────────────────────────────────────────────────────

// ─── Sort options ────────────────────────────────────────────────────────────
type SortOption = 'createdAt_desc' | 'createdAt_asc' | 'receiverName_asc' | 'receiverName_desc';

const SORT_OPTIONS: { value: SortOption; labelKey: string }[] = [
  { value: 'createdAt_desc', labelKey: 'sort_newest_first' },
  { value: 'createdAt_asc', labelKey: 'sort_oldest_first' },
  { value: 'receiverName_asc', labelKey: 'sort_a_z' },
  { value: 'receiverName_desc', labelKey: 'sort_z_a' },
];

const parseSortOption = (v: SortOption): { sortBy: string; order: 'asc' | 'desc' } => {
  const [sortBy, order] = v.split('_') as [string, 'asc' | 'desc'];
  return { sortBy, order };
};

const AllSlotList = ({ overdueOnly = false }: { overdueOnly?: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [packages, setPackages] = useState<PackageData[]>([]);
  const [totalPackages, setTotalPackages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [storageFilter, setStorageFilter] = useState('all');
  const [frameFilter, setFrameFilter] = useState('All');
  const [boxFilter, setBoxFilter] = useState('All');
  const [providerFilter, setProviderFilter] = useState('All');
  const [carrierOptions, setCarrierOptions] = useState<string[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<PackageData | null>(null);
  // Sort: sticky via localStorage
  const [sortOption, setSortOption] = useState<SortOption>(() => {
    return (localStorage.getItem('slotList_sort') as SortOption) || 'createdAt_asc';
  });
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Pagination & Infinite Scroll states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Derive sortBy + order from option
  const { sortBy, order: sortOrder } = parseSortOption(sortOption);

  const handleSortChange = (value: SortOption) => {
    setSortOption(value);
    localStorage.setItem('slotList_sort', value);
    setSortDropdownOpen(false);
  };

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isModalOpen = selectedPackage !== null;
  const searchTimer = useRef<NodeJS.Timeout | null>(null);

  // Handle scroll to top button visibility
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    setShowScrollTop(scrollContainerRef.current.scrollTop > 300);
  }, []);

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle overflow when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const fetchPackages = useCallback(
    async (query?: string, pageToLoad: number = 1) => {
      if (pageToLoad === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const params: Record<string, string> = {};
        const activeQuery = query !== undefined ? query : searchQuery;
        if (activeQuery) params.searchQuery = activeQuery;
        if (storageFilter !== 'all') params.storageOption = storageFilter;
        if (frameFilter !== 'All') params.frameNumber = frameFilter;
        if (boxFilter !== 'All') params.boxNumber = boxFilter;
        if (providerFilter !== 'All') params.carrier = providerFilter;
        params.sortBy = sortBy;
        params.order = sortOrder;

        if (overdueOnly) {
          params.pageSize = '-1';
          const cutoff = new Date();
          cutoff.setUTCDate(cutoff.getUTCDate() - 7);
          cutoff.setUTCHours(0, 0, 0, 0);
          params.to = cutoff.toISOString();
        } else {
          params.pageSize = '20';
          params.pageNumber = pageToLoad.toString();
        }

        const response = await HttpService.get(API_CONFIG.path.packageList, params);
        const filteredData: PackageData[] = Array.isArray(response.data) ? response.data : response.data?.data || [];

        setTotalPackages(response.pagination?.total || 0);
        const sortCarriers = (list: string[]) =>
          [...list].sort((a, b) => {
            const aIsOther = a.toLowerCase() === 'other';
            const bIsOther = b.toLowerCase() === 'other';
            if (aIsOther && !bIsOther) return 1;
            if (!aIsOther && bIsOther) return -1;
            return 0;
          });
        if (Array.isArray(response.filters?.carriers) && response.filters.carriers.length > 0) {
          setCarrierOptions(['All', ...sortCarriers(response.filters.carriers)]);
        } else {
          const fromPackages = Array.from(
            new Set(filteredData.map((p) => p.packageMetadata?.carrier).filter((c): c is string => Boolean(c))),
          );
          if (fromPackages.length > 0) {
            setCarrierOptions(['All', ...sortCarriers(fromPackages)]);
          } else if (pageToLoad === 1) {
            setCarrierOptions(['All']);
          }
        }
        if (pageToLoad === 1) {
          setPackages(filteredData);
        } else {
          setPackages((prev) => {
            const existingIds = new Set(prev.map((p) => p.id));
            const newPackages = filteredData.filter((p) => !existingIds.has(p.id));
            return [...prev, ...newPackages];
          });
        }

        setHasMore(!overdueOnly && filteredData.length === 20);
      } catch {
        if (pageToLoad === 1) setPackages([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [storageFilter, frameFilter, boxFilter, providerFilter, sortBy, sortOrder, searchQuery, overdueOnly],
  );

  // Fetch on filter change
  useEffect(() => {
    setPage(1);
    fetchPackages(undefined, 1);
  }, [storageFilter, frameFilter, boxFilter, providerFilter, sortOption]);

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchPackages(searchQuery, 1);
    }, 500);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll trigger
  useEffect(() => {
    if (inView && hasMore && !loading && !loadingMore && filteredPackages.length > 0) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPackages(undefined, nextPage);
    }
  }, [inView, hasMore, loading, loadingMore]); // Wait, filteredPackages dependency might cause issues.

  const handlePickParcel = async (packageId: string) => {
    let url = `/outbound?packageId=${packageId}`;
    if (searchQuery.trim()) {
      url += `&searchQuery=${encodeURIComponent(searchQuery.trim())}`;
    }
    if (overdueOnly) {
      url += `&mode=return`;
    }
    navigate(url);
  };

  const toggleSelect = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredPackages = useMemo(
    () =>
      packages.filter((pkg) => {
        const matchesStorage = storageFilter === 'all' || pkg.storageOption === storageFilter;
        const matchesFrame = frameFilter === 'All' || pkg.frameNumber === frameFilter;
        const matchesBox = boxFilter === 'All' || pkg.boxNumber === boxFilter;
        const matchesProvider = providerFilter === 'All' || pkg.packageMetadata?.carrier === providerFilter;
        return matchesStorage && matchesFrame && matchesBox && matchesProvider;
      }),
    [packages, storageFilter, frameFilter, boxFilter, providerFilter],
  );

  const selectedPackages = filteredPackages.filter((p) => selectedIds.has(p.id));

  const headerTitle = useMemo(
    () =>
      overdueOnly ? (
        <div className="flex flex-col">
          <span>🕐 {t('overdue_packages_title')}</span>
          <span className="text-xs text-red-500 font-medium">{t('overdue_packages_subtitle')}</span>
        </div>
      ) : (
        <>📦 {t('all_packages_1')}</>
      ),
    [overdueOnly, t],
  );
  const handleHeaderBack = useCallback(() => navigate('/'), [navigate]);

  usePageHeader({ title: headerTitle, onBack: handleHeaderBack });

  return (
    <div className="min-h-[calc(100vh-73px)] bg-background flex flex-col items-center">
      <div className="min-h-[calc(100vh-73px)] relative bg-background px-4 py-3 w-full">
        {/* All Packages content */}
        <div className="space-y-3 pb-2">
          {/* Search + Sort */}
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Search — full width on mobile, flex-1 on desktop */}
            <div className="relative w-full sm:flex-1 sm:basis-0 sm:min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                key={location.key}
                placeholder={t('search_packages')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 bg-white h-[45px] !text-[20px] font-medium py-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Sort + Total — side by side on both mobile and desktop */}
            <div className="flex items-center gap-2 sm:shrink-0">
              {/* Sort Dropdown */}
              <div className="relative flex-1 sm:flex-none w-1/2 sm:w-[150px]">
                <button
                  type="button"
                  onClick={() => setSortDropdownOpen((o) => !o)}
                  onBlur={() => setTimeout(() => setSortDropdownOpen(false), 150)}
                  className="flex items-center gap-1.5 h-8 sm:h-11 w-full px-3 rounded-md border border-border shadow-sm bg-white text-sm font-medium text-foreground hover:bg-gray-50 transition-colors justify-between"
                >
                  <span className="truncate">
                    {t(SORT_OPTIONS.find((o) => o.value === sortOption)?.labelKey ?? 'sort_newest_first')}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-muted-foreground transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {sortDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-background border border-border rounded-md shadow-lg z-50">
                    {SORT_OPTIONS.map(({ value, labelKey }) => (
                      <button
                        key={value}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSortChange(value);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                          sortOption === value ? 'bg-primary/10 text-primary font-semibold' : ''
                        }`}
                      >
                        {t(labelKey)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Total Packages */}
              {/* <div className="bg-white h-10 px-3 rounded-md border border-border shadow-lg shrink-0 flex justify-center items-center text-sm whitespace-nowrap">
                {overdueOnly ? filteredPackages.length : totalPackages} {t('packages')}
              </div> */}

              {/* Carrier filter */}
              <div className="flex-1 sm:flex-none w-1/2 sm:w-[150px]">
                <FilterDropdown
                  value={providerFilter}
                  options={carrierOptions}
                  onChange={setProviderFilter}
                  allLabel={t('all_carriers')}
                  capitalizeFirst
                />
              </div>
            </div>
          </div>
          <div className="flex sm:flex-row flex-col justify-between gap-2">
            {/* Storage tabs */}
            <div className="flex items-center gap-2 sm:w-1/2 w-full">
              <span className="text-sm text-muted-foreground whitespace-nowrap">{t('storage')}</span>
              <div className="grid grid-cols-4 gap-1.5 w-full">
                {STORAGE_FILTERS.map(({ value, labelKey, active, inactive }) => (
                  <button
                    key={value}
                    onClick={() => setStorageFilter(value)}
                    className={`px-2.5 py-1.5 sm:py-2 text-xs font-medium rounded-md transition-colors flex items-center justify-center text-center leading-tight min-h-[32px] sm:min-h-[36px] ${storageFilter === value ? active : inactive}`}
                  >
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Frame and Box dropdowns and action buttons */}
            <div className="flex items-center gap-2 sm:w-1/2 w-full">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* Frame Dropdown */}
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <span className="text-sm text-muted-foreground shrink-0">{t('frame')}</span>
                  <FilterDropdown
                    value={frameFilter}
                    options={ALL_FRAMES}
                    onChange={setFrameFilter}
                    allLabel={t('all_frames')}
                    searchable
                    searchPlaceholder={t('search')}
                    noResultsLabel={t('no_frames_found')}
                  />
                </div>

                {/* Box Dropdown */}
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <span className="text-sm text-muted-foreground shrink-0">{t('box')}</span>
                  <FilterDropdown
                    value={boxFilter}
                    options={ALL_BOXES}
                    onChange={setBoxFilter}
                    allLabel={t('all_boxes')}
                    searchable
                    searchPlaceholder={t('search')}
                    noResultsLabel={t('no_boxes_found')}
                  />
                </div>
              </div>

              {/* Action buttons */}
              {selectedIds.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedIds(new Set())}
                  className="h-8 shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1"
                >
                  <X className="w-4 h-4" />
                  {t('deselect_all')}
                </Button>
              )}
            </div>
          </div>
        </div>
        {/* Grid */}
        {filteredPackages.length > 0 && (
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className={`h-[calc(100vh-200px)] overflow-y-auto transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredPackages.map((pkg) => {
                const { bg, border, text } = storageStyle(pkg.storageOption);
                const isSelected = selectedIds.has(pkg.id);

                return (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPackage(pkg);
                    }}
                    key={pkg.id}
                    className={`relative rounded-xl border transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 cursor-pointer p-4 ${
                      isSelected ? 'border-primary bg-blue-50/40' : 'border-border bg-white'
                    }`}
                  >
                    {/* Package number — large & prominent */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {/* Checkbox */}
                        <button
                          type="button"
                          aria-label={isSelected ? t('deselect_package') : t('select_package')}
                          onClick={(e) => toggleSelect(e, pkg.id)}
                          className="p-2 -m-2 flex items-center justify-center shrink-0"
                        >
                          <span
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                              isSelected
                                ? 'bg-primary border-primary text-white'
                                : 'bg-white border-gray-300 hover:border-primary'
                            }`}
                          >
                            {isSelected && <CheckSquare className="w-3.5 h-3.5" />}
                          </span>
                        </button>
                        <p className="text-2xl font-bold leading-none text-foreground calculator-font">
                          {pkg.packageNumber || 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Carrier */}
                        {pkg.packageMetadata?.carrier && (
                          <div
                            className={`flex items-center gap-2 px-2 py-0.5 rounded-full border w-max text-xs ${carrierStyle(pkg.packageMetadata.carrier).bg} ${carrierStyle(pkg.packageMetadata.carrier).border} ${carrierStyle(pkg.packageMetadata.carrier).text}`}
                          >
                            <Truck className="w-4 h-4" />
                            <p className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold first-letter:uppercase">
                              {pkg.packageMetadata.carrier}
                            </p>
                          </div>
                        )}

                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 border border-gray-200 transition-colors shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/slot-result?packageId=${pkg.id}&packageNumber=${encodeURIComponent(pkg.packageNumber || '')}&from=${encodeURIComponent(location.pathname)}`,
                            );
                          }}
                        >
                          <Pencil className="!w-3.5 !h-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* BIN + Storage + Notes — single row, no wrap */}
                    <div className="py-2 flex items-center gap-1.5 min-h-9">
                      {pkg.frameNumber && (
                        <span className="flex items-center text-sm font-bold px-2 py-0.5 rounded-full bg-white border border-primary text-foreground w-fit shrink-0">
                          {pkg.frameNumber}
                        </span>
                      )}
                      {pkg.boxNumber && (
                        <span className="flex items-center text-sm font-bold px-2 py-0.5 rounded-full bg-white border border-indigo-700 foreground w-fit shrink-0">
                          {pkg.boxNumber}
                        </span>
                      )}
                      {pkg.storageOption && (
                        <span
                          className={`flex items-center text-sm font-bold px-2 py-0.5 rounded-full border shrink-0 ${bg} ${border} ${text}`}
                        >
                          {t(pkg.storageOption.toLowerCase())}
                        </span>
                      )}

                      {/* Notes — read-only */}
                      {pkg.packageMetadata?.customNote && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className="flex-1 min-w-0 flex items-center gap-1 ml-2 cursor-default"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <InfoIcon className="w-3.5 h-3.5 shrink-0" />
                              <span className="line-clamp-1 break-words uppercase text-md font-medium text-foreground/80">
                                {pkg.packageMetadata.customNote}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            align="start"
                            avoidCollisions
                            collisionPadding={16}
                            className="z-[60] max-w-[min(24rem,calc(100vw-2rem))] whitespace-pre-wrap break-words bg-neutral-800 text-white border-neutral-800"
                          >
                            {pkg.packageMetadata.customNote}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>

                    {/* Meta info */}
                    <div className="space-y-1 pb-2">
                      <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-sm">
                        <div className="w-7 h-7 rounded-l-sm p-0.5 flex justify-center items-center">
                          <User className="w-5 h-5 text-blue-500" />
                        </div>
                        <span className="text-md font-medium text-foreground p-0.5 truncate max-w-[90%] whitespace-nowrap">
                          {pkg.packageMetadata?.receiver?.name || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                          <p className={`text-md  ${getDaysAgoAndColor(pkg.createdAt, t).colorClass}`}>
                            {formatDate(pkg.createdAt)}
                            {getDaysAgoAndColor(pkg.createdAt, t).message && (
                              <span className={`text-sm ml-1 ${getDaysAgoAndColor(pkg.createdAt, t).colorClass}`}>
                                ({getDaysAgoAndColor(pkg.createdAt, t).message})
                              </span>
                            )}
                          </p>
                        </div>
                        {/* Pick button */}
                        <div className="mt-2">
                          <Button
                            size="sm"
                            className={`w-fit text-sm `}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePickParcel(pkg.id);
                            }}
                          >
                            {overdueOnly ? t('return_package') : t('pick_package')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Infinite Scroll Loader & Trigger — hidden in overdue mode (all loaded at once) */}
            {!overdueOnly && hasMore && (
              <div ref={loadMoreRef} className="py-6 flex justify-center items-center">
                {loadingMore && <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />}
              </div>
            )}
            {!overdueOnly && !hasMore && filteredPackages.length > 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t('no_more_packages', 'No more packages')}
              </div>
            )}
          </div>
        )}

        {/* Empty / loading states */}
        {!loading && filteredPackages.length === 0 && (
          <div className="text-center py-8">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">{t('no_packages_found')}</p>
          </div>
        )}
        {loading && filteredPackages.length === 0 && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
            <p className="text-muted-foreground">{t('loading_packages')}</p>
          </div>
        )}
      </div>{' '}
      {/* closes space-y-3 pb-2 */}
      {/* Scroll To Top Button */}
      {showScrollTop && (
        <div className="absolute bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <Button
            onClick={scrollToTop}
            size="icon"
            className="w-12 h-12 rounded-full shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-gray-200 hover:bg-gray-300 text-gray-700 border border-gray-300"
          >
            <ArrowUp className="w-5 h-5" />
          </Button>
        </div>
      )}
      {/* ── Floating Pick Selected button ─────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 animate-in fade-in slide-in-from-bottom-4 duration-200">
          <Button
            onClick={() => {
              let url = `/outbound?packageId=${selectedPackages[0].id}`;
              if (searchQuery.trim()) {
                url += `&searchQuery=${encodeURIComponent(searchQuery.trim())}`;
              }
              if (overdueOnly) {
                url += `&mode=return`;
              }
              navigate(url, {
                state: {
                  packageData: {
                    bestMatch: selectedPackages[0],
                    sameReceiverPackages: selectedPackages.slice(1),
                  },
                  preselectAll: true,
                },
              });
            }}
            className={`shadow-2xl px-6 py-3 h-auto text-sm font-semibold gap-2 rounded-full text-white ${
              overdueOnly ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            <ShoppingBag className="w-4 h-4" />
            {overdueOnly ? t('return_selected_packages') : t('pick_selected_packages')} ({selectedIds.size})
          </Button>
        </div>
      )}
      {/* Details Modal */}
      <PackageDetailsModal
        isOpen={isModalOpen}
        onClose={() => setSelectedPackage(null)}
        pkg={selectedPackage}
        isEditing={true}
        actionButtonText={overdueOnly ? t('return_package') : t('pick_package')}
        onActionClick={(e) => {
          e.stopPropagation();
          if (selectedPackage) {
            handlePickParcel(selectedPackage.id);
          }
        }}
      />
    </div>
  );
};

export default AllSlotList;
